import omitBy from 'lodash-es/omitBy';
import { serialize } from 'object-to-formdata';

import type {
  AnalyticsSourceFilters,
  BatchOrdersRequest,
  BatchOrdersResponse,
  BillingLinkParams,
  BillingLinkResponse,
  BrandingResponse,
  BridgeToken,
  BridgeTokenParams,
  BridgeTokenSettings,
  BridgeUserLink,
  BridgeUserResponse,
  CategorizedCompaniesType,
  CategorizedCompaniesWidget,
  CompanyResponse,
  CreateBridgeUserParams,
  CreateMemberResult,
  CreateOrderGroupParams,
  CreateReportResponse,
  CreateWebhookParams,
  CurrentCompanyResponse,
  CursorListResult,
  DataSource,
  DataSourcesParams,
  DataSourcesResponse,
  EditOrderRequest,
  EnvType,
  GetBridgeUsersParams,
  GetBridgeUsersResponse,
  GetMembersParams,
  GetPopularCompaniesListIdResponse,
  GetPopularProvidersListIdResponse,
  GetTemplateResponse,
  GetTemplatesRequest,
  GetTemplatesResponse,
  GetTwilioSettings,
  Identity,
  ImpersonateResponse,
  ListResult,
  LogItem,
  LogsFilters,
  Member,
  OptionsResult,
  OrderEventsResponse,
  OrderFileResponse,
  OrderGroup,
  OrderGroupsParams,
  OrderGroupsResponse,
  PagingFilters,
  PerformanceReportsParams,
  PerformanceReportsResponse,
  ProviderBase,
  ProviderStatusesResponse,
  ProvidersListRequest,
  ProvidersListResponse,
  RefreshOrderRequest,
  RefreshOrderResponse,
  RefreshTask,
  RefreshTaskCreateRequest,
  RefreshTaskCreateResponse,
  ReportParams,
  RequestCoverageParams,
  Role,
  ScoringAttributesFileResponse,
  ScoringAttributesReportResponse,
  SelfCertificationResultsResponse,
  StatementsListParams,
  StatementsListResponse,
  SubcompaniesResponse,
  Task,
  TaskItemProvider,
  TasksFilters,
  TemplateSettings,
  TenantCompanyResponse,
  TransactionsResponse,
  TruvSuggestionType,
  UnresolvedIncidentResponse,
  UnresolvedIncidentsParams,
  UpdateCompanyRequest,
  UpdateMemberResult,
  UpdateTwilioSettings,
  UploadedDocumentInfo,
  UsageReportsParams,
  UsageReportsResponse,
  UserKeysResponse,
  UserProperties,
  VerificationReport,
  VerificationType,
  WebhookItem,
  WidgetProvidersRequest,
  WidgetSettings,
} from 'api/types';
import type { BridgeTokenResponse } from 'components/Emulator/utils/types';
import { PRODUCTS_WITH_ACCOUNT } from 'entities/product/lib/common';
import type { ProfileResponse } from 'entities/user/types';
import { isFalsy } from 'shared/lib/data/isFalsy';

import type { BaseApiClient } from './BaseApiClient';
import { baseApiClient } from './BaseApiClient';

export type ImpersonateParams = {
  target: string;
  reason: string;
};

export type ProductTextsResponse = {
  created_at: string;
  field_name:
    | 'verification_email_header'
    | 'verification_email_footer'
    | 'verification_footer_text'
    | 'verification_header_text'
    | 'verification_title_text'
    | 'verification_expired_header'
    | 'verification_expired_text'
    | 'verification_success_header'
    | 'verification_success_text';
  id: string;
  notification_type: string;
  product_type: VerificationType;
  text: string;
  updated_at: string;
};

export const ProductionUserKeyRequiredError = new Error('ProductionUserKeyRequiredError');

export const coreBackendUrl = (import.meta.env.VITE_APP_CORE_API_URL as string) || 'https://prod.truv.com';

export const bridgeUrl = (import.meta.env.VITE_APP_BRIDGE_URL as string) || 'https://cdn.truv.com/bridge.js';

const statusPageId = import.meta.env.VITE_APP_STATUS_PAGE_ID as string;

export class ApiClient {
  constructor(private client: BaseApiClient) {}

  getStatusPageIncidents = (params?: UnresolvedIncidentsParams) => {
    return this.client.get<UnresolvedIncidentResponse>(`/v1/public/status-incidents/${statusPageId}/`, params);
  };
  getBillingLink = (params: BillingLinkParams) => {
    return this.client.post<BillingLinkResponse>('/v1/billing_link/', params);
  };

  getBrandingSettings = () => {
    return this.client.get<BrandingResponse>('/v1/branding-settings/');
  };

  getProductTexts = () => {
    return this.client.get<ProductTextsResponse[]>('/v1/product-texts/');
  };

  getCompany = () => {
    return this.client.get<CurrentCompanyResponse>('/v1/company/');
  };

  getTenantCompanies = () => {
    return this.client.get<TenantCompanyResponse[]>('/v1/companies/');
  };

  getCompanySettings = (id: string) => {
    return this.client.get<CompanyResponse>(`/v1/companies/${id}/`);
  };

  updateCompanySettings = (id: string, data: UpdateCompanyRequest) => {
    return this.client.patch<CompanyResponse>(`/v1/companies/${id}/`, data);
  };

  getProfile = () => {
    return this.client.get<ProfileResponse>('/v1/profile/');
  };

  getUsageReports = (params: UsageReportsParams) => {
    return this.client.get<UsageReportsResponse>('/p/v1/usage-reports/', params);
  };

  getPerformanceReports = (params: PerformanceReportsParams) => {
    return this.client.get<PerformanceReportsResponse>('/v2/performance-reports/', omitBy(params, isFalsy));
  };

  getLogs = (params: LogsFilters = { page_size: 10, page: 1 }, { signal }: Partial<RequestInit> = {}) => {
    return this.client.get<ListResult<LogItem>>('/v2/logs/', params, { signal });
  };

  getOptions = () => {
    return this.client.get<OptionsResult>('/v1/options/');
  };

  getProviders = () => {
    return this.client.get<TaskItemProvider[]>('/v1/providers/');
  };

  getTasks = (params: TasksFilters = {}, { signal }: Partial<RequestInit> = {}) => {
    return this.client.get<CursorListResult<Task>>('/p/v1/tasks/', params, { signal });
  };

  downloadTasksCSV = (params: TasksFilters) => {
    return this.client.download<Blob>(
      '/p/v1/tasks/',
      {
        ...params,
        format: 'csv',
      },
      'GET',
      'text/csv',
    );
  };

  requestTasksCSVExport = async (
    params: TasksFilters,
    email?: string,
  ): Promise<{ type: 'download'; data: Blob } | { type: 'email' }> => {
    const response = await this.client.downloadWithStatus<Blob>(
      '/p/v1/tasks/',
      {
        ...params,
        format: 'csv',
        ...(email && { email }),
      },
      'GET',
      'text/csv',
    );

    if (response.status === 202) {
      return { type: 'email' };
    }

    return { type: 'download', data: response.data as Blob };
  };

  downloadOrdersCSV = (params: OrderGroupsParams) => {
    return this.client.download<Blob>(
      '/v1/order_groups/',
      {
        ...params,
        format: 'csv',
      },
      'GET',
      'text/csv',
    );
  };

  downloadAllUsersCSV = (params: GetBridgeUsersParams) => {
    return this.client.download<Blob>(
      '/p/v1/users/',
      {
        ...params,
        list_links: true,
        format: 'csv',
      },
      'GET',
      'text/csv',
    );
  };

  getTask = (id: string) => {
    return this.client.get<Task>(`/p/v1/tasks/${id}/`);
  };

  getReport = (params: ReportParams) => {
    return this.client.post<VerificationReport>(`/v1/reports/${params.product_type}/`, params);
  };

  refreshTask = (params: RefreshTaskCreateRequest) => {
    return this.client.post<RefreshTaskCreateResponse>('/p/v1/refresh/tasks/', params);
  };

  getRefreshTask = (id: string) => {
    return this.client.get<RefreshTask>(`/p/v1/refresh/tasks/${id}/`);
  };

  getUserKeys = () => {
    return this.client.get<UserKeysResponse>('/v2/user_keys/');
  };

  getProviderStatuses = () => {
    return this.client.get<ProviderStatusesResponse>('/v1/provider-statuses/', { days: 30 }); // TODO UX-1515
  };

  createBridgeUser = (data: CreateBridgeUserParams) => {
    return this.client.post<BridgeUserResponse>('/p/v1/users/', data);
  };

  getBridgeUser = (id: string) => {
    return this.client.get<BridgeUserResponse>(`/p/v1/users/${id}`, {
      list_links: true,
    });
  };

  getBridgeUsers = (params: GetBridgeUsersParams) => {
    return this.client.get<GetBridgeUsersResponse>('/p/v1/users/', {
      ...params,
      list_links: true,
    });
  };

  createBridgeToken = async (
    userId: string,
    { type, mappingId, providerId, template, account, dataSources, dataSourceFlow }: BridgeTokenParams,
  ) => {
    try {
      const requestParameters: BridgeTokenSettings = {
        product_type: type,
      };

      if (mappingId) {
        requestParameters.company_mapping_id = mappingId;
      }

      if (providerId) {
        requestParameters.provider_id = providerId;
      }

      if (template && template !== 'default') {
        requestParameters.template_id = template;
      }

      if (PRODUCTS_WITH_ACCOUNT.includes(type)) {
        requestParameters.account = { ...account };

        // Backend API return 400 error if bank_address is empty string
        // But no bank_address is allowed
        if (requestParameters.account.bank_address === '') {
          delete requestParameters.account.bank_address;
        }
      }

      if (dataSources) {
        requestParameters.data_sources = dataSources.split(',') as DataSource[];
      }

      if (dataSourceFlow) {
        requestParameters.data_source_flow = dataSourceFlow;
      }

      return this.client.post<BridgeToken>(`/p/v1/users/${userId}/tokens/`, requestParameters);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      // TODO: move error codes to one file later
      if ((e as { code: string }).code === 'production_user_key_required') {
        throw ProductionUserKeyRequiredError;
      } else {
        throw e;
      }
    }
  };

  getBridgeTokenSettings = async (bridgeToken: string, { signal }: Partial<RequestInit> = {}) => {
    return this.client.request<BridgeTokenResponse>(`${coreBackendUrl}/v1/bridge-tokens/${bridgeToken}/`, {
      headers: { 'X-Bridge-Token': bridgeToken },
      signal,
    });
  };

  getCompanySuggests = async (params: { query: string; product_type: VerificationType }) => {
    return this.client.get<TruvSuggestionType[]>('/p/v1/company-mappings-search/', omitBy(params, isFalsy));
  };

  getCategorizedCompanies = async (productType: VerificationType) => {
    return this.client.get<CategorizedCompaniesType[]>('/v1/categorized-companies/', {
      product_type: productType,
    });
  };

  getWidgetCategorizedCompanies = async (bridgeToken: string, listId?: string) => {
    return this.client.request<CategorizedCompaniesWidget[]>(
      `${coreBackendUrl}/v1/categorized-companies/`,
      {
        headers: { 'X-Bridge-Token': bridgeToken },
      },
      {
        format: 'json',
        popular_listing_id: listId ?? '',
      },
    );
  };

  getWidgetProviders = async ({ bridgeToken, productType, dataSource, listId }: WidgetProvidersRequest) => {
    const response = await this.client.request<{ providers: ProviderBase[] }>(`${coreBackendUrl}/v1/bridge/action/`, {
      method: 'POST',
      headers: { 'X-Bridge-Token': bridgeToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        format: 'json',
        popular_provider_listing_id: listId,
        product_type: productType,
        data_source: dataSource,
      }),
    });

    return response.providers;
  };

  getProvidersList = (params: ProvidersListRequest) => {
    return this.client.get<ProvidersListResponse>('/p/v1/providers/', params);
  };

  createOrderGroup = async ({
    order_group,
    employments,
    financial_institutions,
    env,
    reports,
  }: Partial<CreateOrderGroupParams>) => {
    const data = {
      ...order_group,
      ...(employments?.length ? { employers: employments } : undefined),
      ...(financial_institutions?.length ? { financial_institutions } : undefined),
      reports,
      env,
    };

    try {
      const result = await this.client.post<OrderGroup>('/v1/order_groups/ ', data);

      return result;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      // TODO: move error codes to one file later
      if ((e as { code: string }).code === 'production_user_key_required') {
        throw ProductionUserKeyRequiredError;
      } else {
        throw e;
      }
    }
  };

  updateBrandingSettings = async (data: Partial<BrandingResponse>) => {
    return this.client.patch<BrandingResponse>('/v1/branding-settings/', data);
  };

  updateProfile = async (data: Partial<ProfileResponse>) => {
    return this.client.patch<ProfileResponse>('/v1/profile/', data);
  };

  updateWidgetSettings = async (data: Partial<WidgetSettings>) => {
    return this.client.patch<WidgetSettings>('/v1/widget-settings/', data);
  };

  deleteLogo = async () => {
    return this.client.delete<WidgetSettings>('/v1/widget-settings/logo/');
  };

  uploadLogo = async (file: File) => {
    const formData = new FormData();

    formData.append('logo', file, file.name);

    return this.client.patch<WidgetSettings>('/v1/widget-settings/logo/', formData);
  };

  getWidgetSettings = async () => {
    return this.client.get<WidgetSettings>('/v1/widget-settings/');
  };

  getDataSources = async (params?: DataSourcesParams, { signal }: Partial<RequestInit> = {}) => {
    return this.client.get<DataSourcesResponse>('/v2/data-sources/', params, { signal });
  };

  getOrderGroups = async (data?: OrderGroupsParams, { signal }: Partial<RequestInit> = {}) => {
    return this.client.get<OrderGroupsResponse>('/v1/order_groups/', data, { signal });
  };

  getOrderGroup = async (order_group_id: string) => {
    return this.client.get<OrderGroup>(`/v1/order_groups/${order_group_id}/`);
  };

  cancelOrderGroup = async (order_group_id: string) => {
    return this.client.post<OrderGroup>(`/v1/order_groups/${order_group_id}/cancel/`, {});
  };

  cancelSubOrder = async (orderId: string, subOrderId: string) => {
    return this.client.put<OrderGroup>(`/p/v1/orders/${orderId}/employers/${subOrderId}/cancel`, {});
  };

  refreshOrderGroup = async (order_group_id: string, body: RefreshOrderRequest) => {
    return this.client.post<RefreshOrderResponse>(`/v1/order_groups/${order_group_id}/`, body);
  };

  downloadOrderGroupZip = (orderGroupId: string) => {
    return this.client.download<Blob>(
      `/v1/order_groups/${orderGroupId}/bulk_download_files/`,
      undefined,
      'POST',
      'application/zip',
    );
  };

  editOrderGroup = async (order_group_id: string, data: EditOrderRequest) => {
    return this.client.patch<OrderGroup>(`/v1/order_groups/${order_group_id}/`, data);
  };

  createBatchOrders = async (data: BatchOrdersRequest) => {
    const formData = serialize(data, {
      dotsForObjectNotation: true,
      noAttributesWithArrayNotation: true,
    });

    return this.client.post<BatchOrdersResponse>('/v1/bulk/order_groups/', formData);
  };

  getBatchOrders = async (id?: string) => {
    return this.client.get<BatchOrdersResponse>(`/v1/bulk/order_groups/${id}/`);
  };

  createBatchOrdersRun = async (id?: string) => {
    return this.client.post<BatchOrdersResponse>(`/v1/bulk/order_groups/${id}/run/`, {});
  };

  getOrderInvoice = async (orderId: string) => {
    return this.client.get<OrderFileResponse>(`/p/v1/orders/${orderId}/invoice/`);
  };

  getOrderConsent = async (orderId: string) => {
    return this.client.get<OrderFileResponse>(`/p/v1/orders/${orderId}/consent-form/`);
  };

  getOrderEvents = async (orderId: string, includeRefreshOrders?: boolean) => {
    return this.client.get<OrderEventsResponse>(`/p/v1/orders/${orderId}/events/`, {
      include_refresh_orders: includeRefreshOrders,
    });
  };

  getOrderCertifications = async (orderId: string) => {
    return this.client.get<SelfCertificationResultsResponse>(`/p/v1/orders/${orderId}/certifications/`);
  };

  getRoles = async () => {
    return this.client.get<Role[]>('/v1/roles/');
  };

  getMembers = async (params?: GetMembersParams, { signal }: Partial<RequestInit> = {}) => {
    return this.client.get<ListResult<Member>>(
      '/v2/members/',
      // can't use isFalsy because of is_approved: false
      omitBy(params, (value) => typeof value === 'undefined'),
      { signal },
    );
  };

  getMember = async (user_id: string) => {
    return this.client.get<Member>(`/v2/members/${user_id}/`);
  };

  createMember = async (data: Omit<CreateMemberResult, 'user_id'>) => {
    return this.client.post<CreateMemberResult>('/v2/members/', data);
  };

  deleteMember = async (user_id: string) => {
    return this.client.delete<void>(`/v2/members/${user_id}/`);
  };

  updateMember = async (
    user_id: string,
    data: {
      first_name: string;
      last_name: string;
      role: string;
      access_to_templates?: string[];
    },
  ) => {
    return this.client.patch<UpdateMemberResult>(`/v2/members/${user_id}/`, data);
  };

  resendInvite = async (user_id: string) => {
    return this.client.post<Member>(`/v2/members/${user_id}/resend/`, {});
  };

  approveMember = async (user_id: string, approved: boolean) => {
    return this.client.patch<void>(`/v2/members/${user_id}/approve/`, {
      is_approved: approved,
    });
  };

  createUserKeys = async (env: EnvType) => {
    return this.client.post('/v1/user_keys/', { env_type: env });
  };

  deleteUserKey = async (id: string) => {
    return this.client.delete(`/v1/user_keys/${id}/`);
  };

  getProperties = async () => {
    return this.client.get<UserProperties>('/v1/user/properties/');
  };

  updateProperties = async (data: Partial<UserProperties>) => {
    return this.client.patch<Partial<UserProperties>>('/v1/user/properties/', data);
  };

  deleteProperties = async (data: { [key: string]: null }) => {
    return this.client.delete<Partial<UserProperties>>('/v1/user/properties/', data);
  };

  impersonate = async (data: ImpersonateParams) => {
    return this.client.post<ImpersonateResponse>('/v1/impersonations/', data);
  };

  getWebhooks = async (params: Partial<PagingFilters>) => {
    return this.client.get<ListResult<WebhookItem>>('/p/v1/webhooks/', params);
  };

  getWebhook = async (id: string) => {
    return this.client.get<WebhookItem>(`/p/v1/webhooks/${id}`);
  };

  createWebhook = async (data: CreateWebhookParams) => {
    return this.client.post<WebhookItem>('/p/v1/webhooks/', data);
  };

  updateWebhook = async (id: string, data: Partial<CreateWebhookParams>) => {
    return this.client.patch<WebhookItem>(`/p/v1/webhooks/${id}/`, data);
  };

  deleteWebhook = async (id: string) => {
    return this.client.delete<void>(`/p/v1/webhooks/${id}/`);
  };

  getTemplatesList = (
    params: GetTemplatesRequest = { page_size: 10, page: 1 },
    { signal }: Partial<RequestInit> = {},
  ) => {
    return this.client.get<GetTemplatesResponse>('/p/v1/templates/', params, { signal });
  };

  createTemplate = async (data: TemplateSettings) => {
    return this.client.post<GetTemplateResponse>('/p/v1/templates/', data);
  };

  updateTemplate = async (id: string, data: Partial<TemplateSettings>) => {
    return this.client.patch<GetTemplateResponse>(`/p/v1/templates/${id}/`, data);
  };

  getTemplate = (id: string) => {
    return this.client.get<GetTemplateResponse>(`/p/v1/templates/${id}/`);
  };

  deleteTemplate = (id: string) => {
    return this.client.delete<void>(`/p/v1/templates/${id}/`);
  };

  uploadPopularCompaniesList = (file: File) => {
    const formData = new FormData();

    formData.append('file', file, file.name);

    return this.client.post<GetPopularCompaniesListIdResponse>('/p/v1/top-companies/', formData);
  };

  uploadPopularProvidersList = (file: File, dataSource: DataSource, productType: VerificationType) => {
    const formData = new FormData();

    formData.append('file', file, file.name);
    formData.append('data_source', dataSource);
    formData.append('product_types', productType);

    return this.client.post<GetPopularProvidersListIdResponse>('/p/v1/top-providers/', formData);
  };

  getTwilioSettings = () => {
    return this.client.get<GetTwilioSettings>('/v1/twilio-settings/');
  };

  updateTwilioSettings = (data: UpdateTwilioSettings) => {
    return this.client.patch<GetTwilioSettings>('/v1/twilio-settings/', data);
  };

  deleteTwilioSettings = () => {
    return this.client.delete<GetTwilioSettings>('/v1/twilio-settings/');
  };

  getScoringAttributesFile = () => {
    return this.client.get<ScoringAttributesFileResponse>('/v1/scoring-attributes-file/');
  };

  requestCoverage = (data: RequestCoverageParams) => {
    return this.client.post('/v1/coverage-requests/', data);
  };

  getSubcompanies = () => {
    return this.client.get<SubcompaniesResponse>('/v1/subcompanies/');
  };

  getAnalyticsSource = <T>(source: string, params: AnalyticsSourceFilters) => {
    return this.client.get<T>(`/v1/analytics/sources/${source}/`, params);
  };

  createScoringAttributesReport = (data: Record<string, unknown>) => {
    return this.client.post<CreateReportResponse>('/p/v1/scoring_attributes/reports/', data);
  };

  getScoringAttributesReport = (id: string) => {
    return this.client.get<ScoringAttributesReportResponse>(`/p/v1/scoring_attributes/reports/${id}`);
  };

  getPlatformKeys = <TData>(id: string) => {
    return this.client.get<TData>(`/v1/platform-keys/${id}/`);
  };

  createPlatformKeys = <TData, TVariables>(id: string, data: TVariables) => {
    return this.client.post<TData, TVariables>(`/v1/platform-keys/${id}/`, data);
  };

  updatePlatformKeys = <TData, TVariables>(id: string, data: TVariables) => {
    return this.client.patch<TData, TVariables>(`/v1/platform-keys/${id}/`, data);
  };

  getLinkReport = (linkId: string, productType: VerificationType | 'direct_deposit') => {
    return this.client.get<VerificationReport | TransactionsResponse>(
      `/p/v1/links/${linkId}/${productType}${productType === 'transactions' ? '/' : '/report/'}`,
    );
  };

  getLinkUploadedDocuments = (linkId: string) => {
    return this.client.get<UploadedDocumentInfo[]>(`/p/v1/links/${linkId}/documents/`);
  };

  getLinkStatements = (linkId: string, params: StatementsListParams) => {
    return this.client.get<StatementsListResponse>(`/p/v1/links/${linkId}/statements/`, params);
  };

  getLinkIdentity = (linkId: string) => {
    return this.client.get<Identity>(`/p/v1/links/${linkId}/identity/`);
  };

  getLink = (linkId: string) => {
    return this.client.get<BridgeUserLink>(`/p/v1/links/${linkId}/`);
  };

  getDisposableEmailDomains = async (): Promise<Set<string>> | never => {
    const url = 'https://raw.githubusercontent.com/disposable/disposable-email-domains/master/domains.txt';
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch disposable email domains from ${url}`);
    }
    const text = await response.text();
    const domains = text
      .split('\n')
      .map((line) => line.trim().toLowerCase())
      .filter((line) => line.length > 0 && !line.startsWith('#'));
    return new Set(domains);
  };

  getPublicEmailDomains = async (): Promise<Set<string>> | never => {
    const url = 'https://d1uvo9h6rsmxet.cloudfront.net/signup/public-domains.txt';
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch public email domains from ${url}`);
    }
    const text = await response.text();
    const domains = text
      .split('\n')
      .map((line) => line.trim().toLowerCase())
      .filter((line) => line.length > 0 && !line.startsWith('#'));
    return new Set(domains);
  };
}

export const apiClient = new ApiClient(baseApiClient);
